import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { SelectStyles } from '../../../../../../../components/Select/Select.theme'
import { useThemeMode } from '../../../../../../../_metronic/partials'
import { KTIcon } from '../../../../../../../_metronic/helpers'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { formatSum } from '../../../../../../../utils'
import { createDepositDispute, getDisputeReasonLookup } from '../../core/api'
import { DISPUTE_CREATE_OUTCOMES } from '../../core/consts'
import { buildDisputeErrorText, disputeErrorFromException } from '../../core/errors'
import { CreateDisputeRequest, DisputeCreateResolution } from '../../core/requests.types'
import { DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'

/**
 * Поля берутся из MatchingPaymentTicket. У тикета в FILES_VERIFICATION своей суммы (amount) ещё нет
 * — показываем запрошенную клиентом (expectedAmount) и выданную к оплате (requisiteAmount).
 * Валюты в строке нет, на этой странице RUB.
 */
export interface DisputeModalContext {
  paymentTicketToken: string
  expectedAmount: number | null
  /** Absent when the caller does not know the issued amount — then only the requested one is shown. */
  requisiteAmount?: number | null
  currency: string
}

interface Props {
  show: boolean
  onClose: () => void
  context: DisputeModalContext
  onCreated?: (record: DisputeRecord, resolution: DisputeCreateResolution) => void
}

interface FormShape {
  reasonId: number | ''
  comment: string
  holdChunkIndefinitely: boolean
  resolution: DisputeCreateResolution
  amount: string
}

const schema = Yup.object().shape({
  reasonId: Yup.number().integer().min(1).required('Выберите причину'),
  comment: Yup.string().max(1000, 'Не более 1000 символов'),
  // The credited amount is typed by staff only for an immediate success, never prefilled.
  amount: Yup.string().when('resolution', {
    is: 'SUCCESS',
    then: (s) =>
      s
        .required('Введите сумму')
        .test('positive', 'Сумма должна быть больше нуля', (v) => Number(v) > 0),
  }),
})

export const DisputeModal: React.FC<Props> = ({ show, onClose, context, onCreated }) => {
  const { mode: theme } = useThemeMode()
  const [reasons, setReasons] = useState<DisputeReasonLookupItem[]>([])
  const [generalError, setGeneralError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const formik = useFormik<FormShape>({
    initialValues: {
      reasonId: '',
      comment: '',
      holdChunkIndefinitely: false,
      resolution: 'NONE',
      amount: '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      setSubmitting(true)
      setGeneralError(null)
      try {
        const body: CreateDisputeRequest = {
          paymentTicketToken: context.paymentTicketToken,
          reasonId: Number(values.reasonId),
          ...(values.comment ? { comment: values.comment } : {}),
          resolution: values.resolution,
          ...(values.resolution === 'NONE'
            ? { holdChunkIndefinitely: values.holdChunkIndefinitely }
            : {}),
          ...(values.resolution === 'SUCCESS' ? { amount: Number(values.amount) } : {}),
        }
        const response = await createDepositDispute(body)

        if (response.data?.success && response.data.data) {
          onCreated?.(response.data.data, values.resolution)
          onClose()
          return
        }

        setGeneralError(buildDisputeErrorText(response.data, 'Не удалось создать диспут'))
      } catch (e: unknown) {
        setGeneralError(disputeErrorFromException(e, 'Не удалось создать диспут'))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!show) {
      formik.resetForm()
      setGeneralError(null)
      return
    }
    getDisputeReasonLookup().then((res) => {
      if (res.data?.success && res.data.data) {
        setReasons(res.data.data)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const requisiteAmount = context.requisiteAmount ?? null
  const currentReason = reasons.find((r) => r.value === formik.values.reasonId) ?? null
  const outcome =
    DISPUTE_CREATE_OUTCOMES.find((o) => o.value === formik.values.resolution) ??
    DISPUTE_CREATE_OUTCOMES[0]

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Создать диспут</Modal.Title>
      </Modal.Header>
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <div className='mb-2'>
            <div className='text-muted small'>Тикет</div>
            <div className='text-monospace'>{context.paymentTicketToken}</div>
          </div>
          <div className='d-flex gap-6 mb-4'>
            <div>
              <div className='text-muted small'>Клиент заказал</div>
              <div>
                {context.expectedAmount !== null
                  ? `${formatSum(context.expectedAmount)} ${context.currency}`
                  : '—'}
              </div>
            </div>
            {requisiteAmount !== null && (
              <div>
                <div className='text-muted small'>К оплате по реквизиту</div>
                <div>{`${formatSum(requisiteAmount)} ${context.currency}`}</div>
              </div>
            )}
          </div>

          <div className='mb-4'>
            <div className='form-label required'>Исход</div>
            {DISPUTE_CREATE_OUTCOMES.map((o) => (
              <div key={o.value} className='form-check form-check-custom mb-2'>
                <input
                  type='radio'
                  id={`dispute-outcome-${o.value}`}
                  name='resolution'
                  className='form-check-input'
                  checked={formik.values.resolution === o.value}
                  onChange={() => formik.setFieldValue('resolution', o.value)}
                  disabled={submitting}
                />
                <label className='form-check-label' htmlFor={`dispute-outcome-${o.value}`}>
                  {o.label}
                </label>
              </div>
            ))}
          </div>

          <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mb-4'>
            <KTIcon iconName='information-5' className='fs-2x text-warning me-3' />
            <div className='fs-7 text-gray-700'>{outcome.notice}</div>
          </div>

          <div className='mb-3'>
            <label className='form-label required'>Причина</label>
            <Select<DisputeReasonLookupItem>
              options={reasons}
              value={currentReason}
              onChange={(opt) => formik.setFieldValue('reasonId', opt?.value ?? '')}
              placeholder='Выберите причину'
              isClearable={false}
              classNamePrefix='dispute-reason'
              aria-label='reason-select'
              styles={SelectStyles(theme)}
            />
            {formik.touched.reasonId && formik.errors.reasonId && (
              <div className='text-danger small'>{formik.errors.reasonId as string}</div>
            )}
          </div>

          <div className='mb-3'>
            <label className='form-label'>Комментарий</label>
            <textarea
              className='form-control'
              rows={3}
              maxLength={1000}
              {...formik.getFieldProps('comment')}
            />
            {formik.touched.comment && formik.errors.comment && (
              <div className='text-danger small'>{formik.errors.comment as string}</div>
            )}
          </div>

          {/*
            CPAYMENT-1857: обычно выплата отпускает спорный депозит через период охлаждения из
            профиля мэтчинга. Галочка означает «не отпускать вовсе» — выплата ждёт, пока оператор
            не освободит депозит вручную из меню действий. A dispute resolved at creation holds
            nothing, so the checkbox exists only while the dispute stays open.
          */}
          {formik.values.resolution === 'NONE' && (
            <div className='form-check form-check-custom mb-3'>
              <input
                type='checkbox'
                id='holdChunkIndefinitely'
                className='form-check-input'
                checked={formik.values.holdChunkIndefinitely}
                onChange={(e) => formik.setFieldValue('holdChunkIndefinitely', e.target.checked)}
              />
              <label className='form-check-label' htmlFor='holdChunkIndefinitely'>
                Не освобождать чанк (депозит для выплаты)
              </label>
            </div>
          )}

          {formik.values.resolution === 'SUCCESS' && (
            <div className='mb-3'>
              <label className='form-label required' htmlFor='dispute-create-amount'>
                Сумма зачисления
              </label>
              <input
                id='dispute-create-amount'
                name='amount'
                type='number'
                step='0.01'
                min='0'
                className='form-control'
                placeholder='Сумма из чека'
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete='off'
              />
              {formik.touched.amount && formik.errors.amount && (
                <div className='text-danger small mt-1'>{formik.errors.amount}</div>
              )}
              <div className='form-text'>
                Введите сумму по чеку — она зачислится на тикет. Значения выше приведены для сверки
                и не подставляются: расхождение с ними обычно и есть предмет спора.
              </div>
            </div>
          )}

          {generalError && <div className='alert alert-danger py-2'>{generalError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={submitting}>
            {submitting ? 'Создаём…' : outcome.submit}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default DisputeModal
